import { useState } from 'react';
import { TIER_NAMES, TIER_PRICES } from '../config/tierDefaults';
import { T } from '../theme';
import { startCheckout } from '../utils/billingStorage';

const TIER_COLORS = {
  basic:   T.slate,
  starter: T.tealDeep,
  pro:     T.amberDeep,
};

function TierBadge({ tier }) {
  if (!tier) return null;
  return (
    <span style={{
      display: 'inline-block', padding: '3px 10px', borderRadius: 20,
      fontSize: 11, fontWeight: 800, color: '#fff', letterSpacing: 0.5,
      background: TIER_COLORS[tier] || '#6B7280',
    }}>
      {TIER_NAMES[tier] || tier}
    </span>
  );
}

function UpgradePrompt({ promptProps, onClose }) {
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [checkoutError, setCheckoutError]     = useState(null);

  if (!promptProps) return null;
  const { featureName, message, upgradeTo, upgradeMessage } = promptProps;

  const handleUpgrade = async () => {
    if (!upgradeTo || upgradeTo === 'basic') return;
    setCheckoutLoading(true);
    setCheckoutError(null);
    try {
      await startCheckout(upgradeTo); // redirects — component unmounts
    } catch (err) {
      console.error('Checkout error:', err);
      setCheckoutError(err.message || 'Could not open checkout. Please try again.');
      setCheckoutLoading(false);
    }
  };

  return (
    <div
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
        display: 'flex', alignItems: 'flex-end', justifyContent: 'center',
        zIndex: 1200, backdropFilter: 'blur(4px)',
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: '#fff', borderRadius: '20px 20px 0 0',
          padding: '28px 20px 20px', width: '100%', maxWidth: 390,
          animation: 'slideUp 0.3s ease-out',
        }}
        onClick={e => e.stopPropagation()}
      >
        <div style={{ textAlign: 'center', marginBottom: 20 }}>
          <div style={{ fontSize: 40, marginBottom: 12 }}>🔒</div>
          <h3 style={{ margin: '0 0 8px', fontSize: 18, fontWeight: 800, color: '#111' }}>
            {featureName}
          </h3>
          <p style={{ margin: 0, fontSize: 14, color: '#6B7280', lineHeight: 1.5 }}>
            {message}
          </p>
        </div>

        {upgradeTo && (
          <div style={{
            background: T.tealPale, border: `1.5px solid ${T.mist}`,
            borderRadius: 12, padding: '14px 16px', marginBottom: 20,
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
              <TierBadge tier={upgradeTo} />
              <span style={{ fontSize: 13, fontWeight: 700, color: '#111' }}>
                {TIER_NAMES[upgradeTo]} — {TIER_PRICES[upgradeTo]}
              </span>
            </div>
            <p style={{ margin: 0, fontSize: 13, color: '#6B7280' }}>
              {upgradeMessage}
            </p>
          </div>
        )}

        {upgradeTo && upgradeTo !== 'basic' ? (
          <button
            onClick={handleUpgrade}
            disabled={checkoutLoading}
            style={{
              width: '100%', padding: '14px', borderRadius: 12, border: 'none',
              background: checkoutLoading ? '#9CA3AF' : T.gradTeal,
              color: '#fff', fontSize: 14, fontWeight: 700,
              cursor: checkoutLoading ? 'not-allowed' : 'pointer',
              marginBottom: 8,
            }}
          >
            {checkoutLoading ? 'Opening checkout…' : `Upgrade to ${TIER_NAMES[upgradeTo]}`}
          </button>
        ) : (
          <button
            onClick={onClose}
            style={{
              width: '100%', padding: '14px', borderRadius: 12, border: 'none',
              background: T.gradTeal,
              color: '#fff', fontSize: 14, fontWeight: 700, cursor: 'pointer',
              marginBottom: 8,
            }}
          >
            Got it
          </button>
        )}

        {checkoutError && (
          <div style={{
            background: '#FEF2F2', border: '1.5px solid #FCA5A5', borderRadius: 10,
            padding: '8px 12px', fontSize: 12, color: '#DC2626', fontWeight: 600, marginBottom: 8,
          }}>
            {checkoutError}
          </div>
        )}

        <button
          onClick={onClose}
          style={{
            width: '100%', padding: '10px', borderRadius: 12, border: 'none',
            background: 'none', color: '#9CA3AF', fontSize: 13, cursor: 'pointer',
          }}
        >
          Not now
        </button>
      </div>
    </div>
  );
}

export default UpgradePrompt;
